export const DEV_SCORING_FORMS__KEY = 'DEV_SCORING_FORMS';
export const LOG_POST_MESSAGES__KEY = 'LOG_POST_MESSAGES';
export const LOG_WEB_SOCKET_MESSAGES__KEY = 'LOG_WEB_SOCKET_MESSAGES__KEY';
export const LOG_READING_WS_MESSAGES__KEY = 'LOG_READING_WS_MESSAGES__KEY';
export const LOG_SCORING_FORM_EMITTER__KEY = 'LOG_SCORING_FORM_EMITTER';
export const LOG_REDUX_ACTIONS__KEY = 'LOG_REDUX_ACTIONS';
export const SKIP_LOGOUT_TIMER__KEY = 'SKIP_LOGOUT_TIMER__KEY';

/**
 * Development-only DICOM request cache.
 *
 * Plain terms
 * -----------
 * Opening a dynamic study means fetching a thousand images one by one, which
 * takes minutes and starts over on every page reload. With this on, the images
 * are kept on disk so only the first look is slow.
 *
 * Technical
 * ---------
 * The flag and the cache name are read on the viewer side by
 * `cornerstone3D/packages/dicomImageLoader/src/imageLoader/internal/devCache.ts`,
 * which spells both out as literals — that package also builds standalone,
 * without this application's path aliases, so it cannot import them. Keep the
 * two in step. Sharing one origin is what makes this work: the viewer iframe is
 * served from `${window.location.origin}/viewer/<version>/`, so its storage is
 * this application's storage.
 */
export const DICOM_CACHE__KEY = 'DICOM_CACHE';
export const DICOM_CACHE_STORAGE_NAME = 'dynamika-dicom-dev';

const browserWindow = typeof window === 'undefined' ? undefined : window;

const getItem = (key: string) => browserWindow?.localStorage.getItem(key) ?? null;
const setItem = (key: string, value: string) => browserWindow?.localStorage.setItem(key, value);

export const DEV_SCORING_FORMS = !!Number(getItem(DEV_SCORING_FORMS__KEY));
export const LOG_POST_MESSAGES = !!Number(getItem(LOG_POST_MESSAGES__KEY));
export const LOG_WEB_SOCKET_MESSAGES = !!Number(getItem(LOG_WEB_SOCKET_MESSAGES__KEY));
export const LOG_READING_WS_MESSAGES = !!Number(getItem(LOG_READING_WS_MESSAGES__KEY));
export const LOG_SCORING_FORM_EMITTER = !!Number(getItem(LOG_SCORING_FORM_EMITTER__KEY));
export const LOG_REDUX_ACTIONS = !!Number(getItem(LOG_REDUX_ACTIONS__KEY));
export const SKIP_LOGOUT_TIMER = !!Number(getItem(SKIP_LOGOUT_TIMER__KEY));

const style = 'font-weight: bold; padding: 4px; border-radius: 4px;';

export const colors = {
  blue: 'background: blue; color: white; ' + style, // PostMessage
  purple: 'background: purple; color: white; ' + style, // ScoringFormEmitter
  cyan: 'background: black; color: cyan; ' + style, // ReadingWS
  green: 'background: green; color: white; ' + style, // ReadingWS
  yellow: 'background: yellow; color: black; ' + style, // WebSocket
  peach: 'background: peachpuff; color: black; ' + style, // WebSocket
  red: 'background: red; color: white; ' + style, // Error
  orange: 'background: orange; color: black; ' + style, // Warning
  pink: 'background: hotpink; color: black; ' + style, // UI Event
  gray: 'background: gray; color: white; ' + style, // Debug
  navy: 'background: navy; color: white; ' + style, // Network
};

const removeParamsFromPath = (path: string) => {
  const lineMatch = path.match(/:(\d+):\d+(?:\))?$/);
  const lineNumber = lineMatch ? `:${lineMatch[1]}` : '';

  return (
    path
      .replace(/\?[^->]+(?=->|$)/g, '') // видаляємо параметри часу
      .replace(/:\d+:\d+(?:\))?$/, '') // видаляємо номери рядків та колонок
      .trim() + lineNumber // додаємо номер рядка
  );
};

const getFileNameFromStack = (from: number = 3, to: number = 6) => {
  const stack = new Error().stack;
  if (!stack) {
    return '';
  }
  const lines = stack.split('\n');
  const callerLines = lines.slice(3, 6);
  const fileNames = callerLines
    .map(line => {
      const match = line.match(/(?:[/\\])([^/\\]+)(?::\d+:\d+)?(?:\?[^:]+)?(?::\d+:\d+)?(?:\))?$/);
      return match ? removeParamsFromPath(match[1]) : '';
    })
    .filter(Boolean);
  return fileNames.reverse().join(' -> ');
};

export const COLOR_LOG = (
  color: keyof typeof colors,
  prefix: string,
  title: string,
  body: unknown,
  arrow = '->'
) => {
  const path = getFileNameFromStack();
  const head = `%cLOG[${prefix}] ${arrow} ${title}`;
  console.groupCollapsed(head, colors[color]);
  console.log(path);
  console.log(body);
  console.groupEnd();
};

function toggleMode(key: string) {
  const value = getItem(key);
  setItem(key, value === '1' ? '0' : '1');
  if (value === '0') {
    console.log(`[${key}] -> enabled`);
  } else {
    console.log(`[${key}] -> disabled`);
  }
}

if (browserWindow?.location.hostname === 'localhost') {
  browserWindow.dev = {
    logs: {
      toggleReadingWSMessages: () => toggleMode(LOG_READING_WS_MESSAGES__KEY),
      toggleWebSocketMessages: () => toggleMode(LOG_WEB_SOCKET_MESSAGES__KEY),
      togglePostMessages: () => toggleMode(LOG_POST_MESSAGES__KEY),
      toggleScoringFormEmitter: () => toggleMode(LOG_SCORING_FORM_EMITTER__KEY),
      toggleReduxActions: () => toggleMode(LOG_REDUX_ACTIONS__KEY),
    },
    toggleScoringForms: () => toggleMode(DEV_SCORING_FORMS__KEY),
    toggleLogoutTimer: () => toggleMode(SKIP_LOGOUT_TIMER__KEY),
    toggleAll: () => {
      toggleMode(LOG_READING_WS_MESSAGES__KEY);
      toggleMode(LOG_WEB_SOCKET_MESSAGES__KEY);
      toggleMode(LOG_POST_MESSAGES__KEY);
      toggleMode(LOG_SCORING_FORM_EMITTER__KEY);
      toggleMode(DEV_SCORING_FORMS__KEY);
      toggleMode(SKIP_LOGOUT_TIMER__KEY);
      toggleMode(LOG_REDUX_ACTIONS__KEY);
    },
  };
}

declare global {
  interface Window {
    dev?: {
      logs: {
        toggleReadingWSMessages: () => void;
        toggleWebSocketMessages: () => void;
        togglePostMessages: () => void;
        toggleScoringFormEmitter: () => void;
        toggleReduxActions: () => void;
      };
      toggleScoringForms: () => void;
      toggleLogoutTimer: () => void;
      toggleAll: () => void;
    };
  }
}
